<template>
  <div class="layout-container">
    <!-- 背景装饰 -->
    <div class="bg-decoration">
      <div class="bg-gradient"></div>
      <div class="bg-mesh"></div>
      <div class="floating-shapes">
        <div class="shape shape-1"></div>
        <div class="shape shape-2"></div>
        <div class="shape shape-3"></div>
        <div class="shape shape-4"></div>
      </div>
    </div>

    <!-- 侧边栏 -->
    <aside class="sidebar" :class="{ 'sidebar-collapsed': isCollapsed }">
      <!-- Logo区域 -->
      <div class="sidebar-header">
        <div class="logo-wrapper">
          <img v-if="!isCollapsed" src="/img/logo-white.png" alt="富福德正" class="logo-image">
          <div v-else class="logo-mini">
            <span class="logo-letter">F</span>
          </div>
        </div>
        <div v-if="!isCollapsed" class="logo-tagline">智能债务管理平台</div>
      </div>
      
      <!-- 菜单区域 -->
      <nav class="sidebar-nav">
        <el-menu
          :default-active="activeMenu"
          :collapse="isCollapsed"
          :collapse-transition="false"
          router
          class="custom-menu"
          @select="handleMenuClick"
        >
          <template v-for="route in filteredRoutes" :key="route.path">
            <!-- 单级菜单（仪表盘） -->
            <el-menu-item v-if="!route.children || route.children.length === 0" :index="route.path" class="menu-item-single">
              <el-icon class="menu-icon"><component :is="route.meta.icon" /></el-icon>
              <template #title>
                <span class="menu-title">{{ route.meta.title }}</span>
              </template>
            </el-menu-item>
            
            <!-- 分组菜单 -->
            <el-sub-menu v-else :index="route.path" class="menu-item-group">
              <template #title>
                <el-icon class="menu-icon"><component :is="route.meta.icon" /></el-icon>
                <span class="menu-title">{{ route.meta.title }}</span>
              </template>
              
              <template v-for="child in route.children" :key="child.path">
                <!-- 二级菜单项（无子菜单） -->
                <el-menu-item
                  v-if="!child.children || child.children.length === 0"
                  :index="`${route.path}/${child.path}`"
                  class="menu-item-child"
                >
                  <el-icon v-if="child.meta.icon" class="menu-icon-sub"><component :is="child.meta.icon" /></el-icon>
                  <span class="menu-title-sub">{{ child.meta.title }}</span>
                </el-menu-item>
                
                <!-- 三级菜单（如债中和） -->
                <el-sub-menu v-else :index="`${route.path}/${child.path}`" class="menu-item-nested">
                  <template #title>
                    <el-icon v-if="child.meta.icon" class="menu-icon-sub"><component :is="child.meta.icon" /></el-icon>
                    <span class="menu-title-sub">{{ child.meta.title }}</span>
                  </template>
                  <el-menu-item
                    v-for="subChild in child.children"
                    :key="subChild.path"
                    :index="`${route.path}/${child.path}/${subChild.path}`"
                    class="menu-item-leaf"
                  >
                    <span class="menu-title-leaf">{{ subChild.meta.title }}</span>
                  </el-menu-item>
                </el-sub-menu>
              </template>
            </el-sub-menu>
          </template>
        </el-menu>
      </nav>
      
      <!-- 底部折叠按钮 -->
      <div class="sidebar-footer">
        <button class="collapse-toggle" @click="toggleCollapse" :title="isCollapsed ? '展开菜单' : '收起菜单'">
          <el-icon :size="20"><Expand v-if="isCollapsed" /><Fold v-else /></el-icon>
        </button>
        <div v-if="!isCollapsed" class="version-info">V 0.2.0</div>
      </div>
    </aside>

    <!-- 主内容区 -->
    <main class="main-container" :class="{ 'main-expanded': isCollapsed }">
      <!-- 顶部导航栏 -->
      <header class="navbar">
        <div class="navbar-left">
          <el-breadcrumb separator="/" class="breadcrumb">
            <el-breadcrumb-item v-for="item in breadcrumbs" :key="item.path">
              <router-link v-if="item.path" :to="item.path" class="breadcrumb-link">
                {{ item.meta.title }}
              </router-link>
              <span v-else class="breadcrumb-current">{{ item.meta.title }}</span>
            </el-breadcrumb-item>
          </el-breadcrumb>
        </div>
        
        <div class="navbar-right">
          <div class="navbar-actions">
            <el-badge :value="unreadCount" :hidden="unreadCount === 0" :max="99" class="action-badge">
              <button class="action-btn" @click="$router.push('/customer-service')">
                <el-icon><ChatDotRound /></el-icon>
              </button>
            </el-badge>
          </div>
          
          <el-dropdown trigger="click" @command="handleUserCommand">
            <div class="user-trigger">
              <div class="user-avatar-wrapper">
                <el-avatar :size="38" :src="adminInfo.avatar" class="user-avatar">
                  {{ adminInfo.name?.charAt(0) || 'A' }}
                </el-avatar>
                <span class="user-status-dot"></span>
              </div>
              <div class="user-info-text">
                <span class="user-name">{{ adminInfo.name || adminInfo.mobile || '管理员' }}</span>
                <span class="user-role">{{ roleMap[adminInfo.role] || '管理员' }}</span>
              </div>
              <el-icon class="dropdown-arrow"><ArrowDown /></el-icon>
            </div>
            <template #dropdown>
              <el-dropdown-menu class="user-dropdown">
                <el-dropdown-item command="profile">
                  <el-icon><User /></el-icon>
                  <span>个人资料</span>
                </el-dropdown-item>
                <el-dropdown-item divided command="logout">
                  <el-icon><SwitchButton /></el-icon>
                  <span>退出登录</span>
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </header>

      <!-- 页面内容 -->
      <div class="content-wrapper">
        <router-view v-slot="{ Component }">
          <transition name="page-fade" mode="out-in">
            <component :is="Component" />
          </transition>
        </router-view>
      </div>
      
      <!-- 页脚 -->
      <footer class="footer">
        <div class="footer-inner">
          <span class="copyright">© 2026 海南富福德正网络科技有限公司</span>
          <span class="footer-divider">·</span>
          <span class="powered">
            Powered by 
            <a href="https://tory.ffdzheng.com" target="_blank" class="powered-link">ToryLiu</a>
          </span>
        </div>
      </footer>
    </main>
    
    <!-- 个人资料弹窗 -->
    <el-dialog 
      v-model="profileVisible" 
      title="个人资料" 
      width="480px"
      class="profile-dialog"
      :close-on-click-modal="false"
    >
      <div class="profile-header">
        <el-avatar :size="80" :src="profileForm.avatar" class="profile-avatar">
          {{ profileForm.name?.charAt(0) || 'A' }}
        </el-avatar>
        <div class="profile-basic">
          <h3>{{ profileForm.name || '管理员' }}</h3>
          <el-tag size="small" type="success">{{ roleMap[profileForm.role] || '管理员' }}</el-tag>
        </div>
      </div>
      <el-form :model="profileForm" label-width="80px" class="profile-form">
        <el-form-item label="用户名">
          <el-input v-model="profileForm.name" placeholder="请输入用户名" />
        </el-form-item>
        <el-form-item label="手机号">
          <el-input v-model="profileForm.mobile" disabled />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="profileVisible = false">取消</el-button>
        <el-button type="primary" @click="saveProfile">保存更改</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Expand, Fold, ArrowDown, User, SwitchButton, ChatDotRound } from '@element-plus/icons-vue'
import adminService from '@/api/admin/index.js'

const route = useRoute()
const router = useRouter()
const store = useStore()

const isCollapsed = ref(false)
const profileVisible = ref(false)
const profileForm = ref({ name: '', mobile: '', avatar: '', role: '' })
const unreadCount = ref(0)

const roleMap = {
  admin: '超级管理员',
  operator: '运营人员',
  support: '客服人员'
}

const adminInfo = computed(() => store.getters['auth/adminInfo'] || {})
const permissions = computed(() => store.getters['auth/permissions'] || [])

const filteredRoutes = computed(() => {
  const routes = router.options.routes.find(r => r.path === '/')?.children || []
  return routes.filter(route => {
    if (!route.meta.permission) return true
    return permissions.value.includes(route.meta.permission) || permissions.value.includes('*')
  })
})

const activeMenu = computed(() => {
  const { meta, path } = route
  if (meta.activeMenu) return meta.activeMenu
  return path || '/'
})

const handleMenuClick = (key) => {
  if (typeof key === 'string') {
    const targetPath = key === '' ? '/' : key
    const fullPath = targetPath.startsWith('/') ? targetPath : `/${targetPath}`
    router.push(fullPath)
  }
}

const breadcrumbs = computed(() => {
  const matched = route.matched.filter(item => item.meta && item.meta.title)
  return matched.map(item => ({ path: item.path, meta: item.meta }))
})

const toggleCollapse = () => {
  isCollapsed.value = !isCollapsed.value
}

const handleUserCommand = (command) => {
  if (command === 'profile') {
    profileForm.value = { ...adminInfo.value }
    profileVisible.value = true
  } else if (command === 'logout') {
    handleLogout()
  }
}

const saveProfile = async () => {
  try {
    const res = await adminService.put(`/users/\${adminInfo.value.id}`, profileForm.value)
    if (res.data?.code === 200) {
      ElMessage.success('保存成功')
      profileVisible.value = false
      store.commit('auth/SET_ADMIN_INFO', { ...adminInfo.value, ...profileForm.value })
    }
  } catch (error) {
    ElMessage.error('保存失败：' + error.message)
  }
}

const handleLogout = () => {
  ElMessageBox.confirm('确定要退出登录吗？', '提示', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning'
  }).then(async () => {
    try {
      await store.dispatch('auth/logout')
      router.push('/login')
      ElMessage.success('退出成功')
    } catch (error) {
      ElMessage.error('退出失败：' + error.message)
    }
  }).catch(() => {})
}

watch(() => route.path, () => {}, { immediate: true })
</script>

<style scoped>
/* ========== 设计系统变量 ========== */
:root {
  --sidebar-width: 260px;
  --sidebar-collapsed: 72px;
  --navbar-height: 64px;
  
  /* 主色调 */
  --color-primary: #0ea5e9;
  --color-primary-dark: #0284c7;
  --color-accent: #10b981;
  --color-accent-light: #34d399;
  
  /* 深色系 */
  --color-deep-1: #070d19;
  --color-deep-2: #0c1629;
  --color-deep-3: #122035;
  --color-deep-4: #1a2d4a;
  
  /* 文字色 */
  --text-primary: #f8fafc;
  --text-secondary: #cbd5e1;
  --text-tertiary: #94a3b8;
  
  /* 玻璃效果 */
  --glass-bg: rgba(15, 30, 50, 0.65);
  --glass-border: rgba(255, 255, 255, 0.08);
  --glass-glow: rgba(14, 165, 233, 0.15);
  
  /* 过渡 */
  --ease-out: cubic-bezier(0.22, 1, 0.36, 1);
  --ease-spring: cubic-bezier(0.34, 1.56, 0.64, 1);
}

/* ========== 布局容器 ========== */
.layout-container {
  display: flex;
  height: 100vh;
  overflow: hidden;
  background: linear-gradient(135deg, #f0f4f8 0%, #e2e8f0 100%);
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", "PingFang SC", sans-serif;
}

/* ========== 背景装饰 ========== */
.bg-decoration {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
}

.bg-gradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #0c1222 0%, #1a365d 50%, #0f2744 100%);
}

.bg-mesh {
  position: absolute;
  inset: 0;
  background-image: 
    radial-gradient(ellipse at 20% 20%, rgba(14, 165, 233, 0.08) 0%, transparent 50%),
    radial-gradient(ellipse at 80% 80%, rgba(16, 185, 129, 0.06) 0%, transparent 50%);
}

.floating-shapes {
  position: absolute;
  inset: 0;
  overflow: hidden;
}

.shape {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.12;
}

.shape-1 {
  width: 500px;
  height: 500px;
  background: linear-gradient(135deg, #0ea5e9, #10b981);
  top: -150px;
  left: -100px;
  will-change: transform;
  /* 动画已禁用以提升性能 */
}

.shape-2 {
  width: 350px;
  height: 350px;
  background: linear-gradient(135deg, #8b5cf6, #0ea5e9);
  bottom: -80px;
  right: 15%;
  will-change: transform;
  /* 动画已禁用以提升性能 */
}

.shape-3 {
  width: 250px;
  height: 250px;
  background: linear-gradient(135deg, #10b981, #0ea5e9);
  top: 50%;
  left: 25%;
  will-change: transform;
  /* 动画已禁用以提升性能 */
}

.shape-4 {
  width: 200px;
  height: 200px;
  background: linear-gradient(135deg, #f59e0b, #10b981);
  top: 15%;
  right: 20%;
  will-change: transform;
  /* 动画已禁用以提升性能 */
}

/* 动画已禁用以提升性能
@keyframes drift {
  0%, 100% { transform: translate(0, 0) rotate(0deg) scale(1); }
  33% { transform: translate(30px, -20px) rotate(5deg) scale(1.02); }
  66% { transform: translate(-20px, 30px) rotate(-3deg) scale(0.98); }
}
*/

/* ========== 侧边栏 ========== */
.sidebar {
  width: var(--sidebar-width);
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 100;
  transition: width 0.4s var(--ease-out);
  background: transparent;
}

.sidebar::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, 
    #0a1628 0%, 
    #0f1f38 30%,
    #0f1f38 70%,
    #0a1628 100%
  );
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 4px 0 30px rgba(0, 0, 0, 0.5);
  z-index: -1;
}

.sidebar-collapsed { width: var(--sidebar-collapsed); }

/* ========== Logo区域 ========== */
.sidebar-header {
  padding: 28px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
}

.sidebar-header::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 20px;
  right: 20px;
  height: 1px;
  background: linear-gradient(90deg, 
    transparent 0%, 
    rgba(255, 255, 255, 0.1) 50%, 
    transparent 100%
  );
}

.logo-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
}

.logo-image {
  height: 44px;
  width: auto;
  filter: brightness(1.1) drop-shadow(0 2px 8px rgba(14, 165, 233, 0.3));
}

.logo-mini {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: linear-gradient(135deg, var(--color-accent) 0%, #059669 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 
    0 4px 20px rgba(16, 185, 129, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

.logo-letter {
  font-size: 22px;
  font-weight: 800;
  color: white;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.logo-tagline {
  margin-top: 10px;
  font-size: 11px;
  font-weight: 500;
  color: #e2e8f0;
  letter-spacing: 1.5px;
  text-transform: uppercase;
}

/* ========== 导航菜单 - 清晰玻璃态 ========== */
.sidebar-nav {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px 12px;
}

.sidebar-nav::-webkit-scrollbar { width: 4px; }
.sidebar-nav::-webkit-scrollbar-track { background: transparent; }
.sidebar-nav::-webkit-scrollbar-thumb { 
  background: rgba(255, 255, 255, 0.1); 
  border-radius: 4px;
}
.sidebar-nav::-webkit-scrollbar-thumb:hover { 
  background: rgba(255, 255, 255, 0.2); 
}

/* Element Plus 菜单重置 */
.custom-menu {
  border: none !important;
  background: transparent !important;
}

.custom-menu :deep(.el-menu-item),
.custom-menu :deep(.el-sub-menu__title) {
  height: 46px;
  line-height: 46px;
  margin: 4px 0;
  padding: 0 16px !important;
  border-radius: 12px;
  color: #e2e8f0;
  background: transparent;
  transition: all 0.3s var(--ease-out);
  font-weight: 500;
  font-size: 14px;
  position: relative;
  overflow: hidden;
}

/* 悬停效果 - 青色发光块 */
.custom-menu :deep(.el-menu-item:hover),
.custom-menu :deep(.el-sub-menu__title:hover) {
  background: linear-gradient(135deg, 
    rgba(14, 165, 233, 0.35) 0%, 
    rgba(16, 185, 129, 0.25) 100%
  );
  color: #ffffff;
  box-shadow: 
    0 0 25px rgba(14, 165, 233, 0.2),
    inset 0 0 0 1px rgba(14, 165, 233, 0.4);
}

/* 激活状态 - 渐变高亮 */
.custom-menu :deep(.el-menu-item.is-active) {
  background: linear-gradient(135deg, var(--color-accent) 0%, #059669 100%);
  color: white;
  box-shadow: 
    0 4px 20px rgba(16, 185, 129, 0.4),
    0 0 0 1px rgba(16, 185, 129, 0.5),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  transform: translateX(4px);
}

.custom-menu :deep(.el-menu-item.is-active) .menu-icon {
  color: white;
  filter: drop-shadow(0 0 4px rgba(255, 255, 255, 0.5));
}

/* 图标样式 */
.menu-icon {
  font-size: 18px;
  margin-right: 14px;
  color: #cbd5e1;
  transition: all 0.3s var(--ease-out);
}

.custom-menu :deep(.el-menu-item:hover) .menu-icon,
.custom-menu :deep(.el-sub-menu__title:hover) .menu-icon {
  color: #22d3ee;
  transform: scale(1.15);
}

.menu-title { 
  font-size: 14px;
  font-weight: 500;
}

/* ========== 子菜单样式 - 清晰背景块 ========== */
.custom-menu :deep(.el-sub-menu) {
  margin: 4px 0;
}

.custom-menu :deep(.el-sub-menu.is-opened) {
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
}

/* 子菜单箭头样式 */
.custom-menu :deep(.el-sub-menu__icon-arrow) {
  color: #94a3b8;
  font-size: 12px;
  transition: all 0.3s var(--ease-out);
}

.custom-menu :deep(.el-sub-menu.is-opened .el-sub-menu__icon-arrow) {
  color: #22d3ee;
  transform: rotate(180deg);
}

.custom-menu :deep(.el-sub-menu__title:hover .el-sub-menu__icon-arrow) {
  color: #22d3ee;
}

.custom-menu :deep(.el-sub-menu .el-menu) {
  background: transparent !important;
  padding: 4px 0 4px 8px;
  margin: 4px 0;
}

/* 子菜单项 - 带清晰背景 */
.custom-menu :deep(.el-sub-menu .el-menu-item) {
  height: 42px;
  line-height: 42px;
  padding-left: 52px !important;
  margin: 3px 8px;
  border-radius: 10px;
  color: #cbd5e1;
  background: rgba(15, 35, 60, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.04);
  font-size: 13px;
  position: relative;
}

/* 子菜单项悬停 - 青色发光 */
.custom-menu :deep(.el-sub-menu .el-menu-item:hover) {
  background: linear-gradient(135deg, 
    rgba(14, 165, 233, 0.4) 0%, 
    rgba(16, 185, 129, 0.3) 100%
  );
  color: #ffffff;
  border-color: rgba(14, 165, 233, 0.5);
  box-shadow: 
    0 0 20px rgba(14, 165, 233, 0.25),
    inset 0 0 0 1px rgba(14, 165, 233, 0.4);
}

/* 子菜单项激活 */
.custom-menu :deep(.el-sub-menu .el-menu-item.is-active) {
  background: linear-gradient(135deg, var(--color-accent) 0%, #059669 100%);
  color: white;
  border-color: transparent;
  box-shadow: 
    0 4px 15px rgba(16, 185, 129, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

/* 子菜单项左侧指示条 */
.custom-menu :deep(.el-sub-menu .el-menu-item)::before {
  content: '';
  position: absolute;
  left: 32px;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--text-tertiary);
  transition: all 0.3s var(--ease-out);
}

.custom-menu :deep(.el-sub-menu .el-menu-item:hover)::before,
.custom-menu :deep(.el-sub-menu .el-menu-item.is-active)::before {
  background: var(--color-primary);
  box-shadow: 0 0 8px var(--color-primary);
}

.menu-title-sub { 
  font-size: 13px;
  font-weight: 500;
}

/* ========== 三级菜单样式（嵌套子菜单如债中和） ========== */
.custom-menu :deep(.menu-item-nested) {
  margin: 2px 0;
}

.custom-menu :deep(.menu-item-nested .el-sub-menu__title) {
  height: 40px;
  line-height: 40px;
  padding-left: 44px !important;
  margin: 2px 8px;
  border-radius: 8px;
  color: #94a3b8;
  background: rgba(10, 20, 40, 0.5);
  font-size: 13px;
}

.custom-menu :deep(.menu-item-nested .el-sub-menu__title:hover) {
  background: linear-gradient(135deg, 
    rgba(14, 165, 233, 0.3) 0%, 
    rgba(16, 185, 129, 0.2) 100%
  );
  color: #ffffff;
}

.custom-menu :deep(.menu-item-nested.is-opened .el-sub-menu__title) {
  color: #22d3ee;
}

.custom-menu :deep(.menu-item-nested .el-menu) {
  background: transparent !important;
  padding: 2px 0 2px 12px;
}

/* 三级菜单项 */
.custom-menu :deep(.menu-item-leaf) {
  height: 36px;
  line-height: 36px;
  padding-left: 60px !important;
  margin: 2px 8px;
  border-radius: 8px;
  color: #94a3b8;
  background: rgba(5, 15, 30, 0.6);
  font-size: 12px;
}

.custom-menu :deep(.menu-item-leaf:hover) {
  background: linear-gradient(135deg, 
    rgba(14, 165, 233, 0.35) 0%, 
    rgba(16, 185, 129, 0.25) 100%
  );
  color: #ffffff;
}

.custom-menu :deep(.menu-item-leaf.is-active) {
  background: linear-gradient(135deg, var(--color-accent) 0%, #059669 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.menu-title-leaf {
  font-size: 12px;
  font-weight: 500;
}

.menu-icon-sub {
  font-size: 14px;
  margin-right: 10px;
  color: #94a3b8;
}

/* ========== 底部折叠区域 ========== */
.sidebar-footer {
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  position: relative;
}

.sidebar-footer::before {
  content: '';
  position: absolute;
  top: 0;
  left: 20px;
  right: 20px;
  height: 1px;
  background: linear-gradient(90deg, 
    transparent 0%, 
    rgba(255, 255, 255, 0.08) 50%, 
    transparent 100%
  );
}

.collapse-toggle {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.06);
  color: #e2e8f0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s var(--ease-out);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.collapse-toggle:hover {
  background: linear-gradient(135deg, 
    rgba(14, 165, 233, 0.35) 0%, 
    rgba(16, 185, 129, 0.25) 100%
  );
  color: #ffffff;
  border-color: rgba(14, 165, 233, 0.4);
  transform: scale(1.05);
}

.version-info {
  font-size: 11px;
  font-weight: 600;
  color: #cbd5e1;
  letter-spacing: 0.5px;
}

/* ========== 主内容区 ========== */
.main-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  z-index: 1;
}

/* ========== 导航栏 ========== */
.navbar {
  height: var(--navbar-height);
  padding: 0 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(180deg, 
    rgba(7, 13, 25, 0.95) 0%, 
    rgba(12, 22, 41, 0.92) 100%
  );
  backdrop-filter: blur(20px);
  border-bottom: 1px solid var(--glass-border);
  position: relative;
  z-index: 50;
}

.breadcrumb {
  font-size: 14px;
}

.breadcrumb-link {
  color: #e2e8f0;
  text-decoration: none;
  transition: all 0.2s;
}

.breadcrumb-link:hover { 
  color: var(--text-primary); 
}

.breadcrumb-current {
  color: var(--text-primary);
  font-weight: 600;
}

.navbar-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.navbar-actions {
  display: flex;
  gap: 8px;
}

.action-badge :deep(.el-badge__content) {
  background: var(--color-accent);
}

.action-btn {
  width: 38px;
  height: 38px;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: #e2e8f0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: all 0.25s var(--ease-out);
}

.action-btn:hover {
  background: rgba(255, 255, 255, 0.08);
  color: var(--text-primary);
}

/* 用户信息 */
.user-trigger {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 14px 6px 6px;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.25s var(--ease-out);
}

.user-trigger:hover {
  background: rgba(255, 255, 255, 0.06);
}

.user-avatar-wrapper {
  position: relative;
}

.user-avatar {
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-accent) 100%);
  font-weight: 600;
  font-size: 15px;
  box-shadow: 0 2px 10px rgba(14, 165, 233, 0.3);
}

.user-status-dot {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 10px;
  height: 10px;
  background: var(--color-accent);
  border: 2px solid var(--color-deep-2);
  border-radius: 50%;
  box-shadow: 0 0 8px var(--color-accent);
}

.user-info-text {
  display: flex;
  flex-direction: column;
}

.user-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  line-height: 1.2;
}

.user-role {
  font-size: 11px;
  color: #cbd5e1;
  line-height: 1.4;
}

.dropdown-arrow {
  color: #cbd5e1;
  transition: transform 0.3s var(--ease-out);
}

.user-trigger:hover .dropdown-arrow {
  transform: rotate(180deg);
  color: #e2e8f0;
}

/* ========== 内容区 ========== */
.content-wrapper {
  flex: 1;
  padding: 24px;
  overflow-y: auto;
  overflow-x: hidden;
}

.page-fade-enter-active,
.page-fade-leave-active {
  transition: all 0.3s var(--ease-out);
}

.page-fade-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

.page-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* ========== 页脚 ========== */
.footer {
  padding: 14px 28px;
  background: linear-gradient(180deg, 
    rgba(12, 22, 41, 0.92) 0%, 
    rgba(7, 13, 25, 0.95) 100%
  );
  border-top: 1px solid var(--glass-border);
}

.footer-inner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 12px;
  color: #cbd5e1;
}

.footer-divider { opacity: 0.3; }

.powered-link {
  color: var(--color-accent);
  text-decoration: none;
  font-weight: 600;
  transition: all 0.2s;
}

.powered-link:hover { 
  color: var(--color-accent-light);
}

/* ========== 弹窗样式 ========== */
.profile-dialog :deep(.el-dialog) {
  border-radius: 20px;
  overflow: hidden;
}

.profile-header {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 8px 0 24px;
  border-bottom: 1px solid #f1f5f9;
  margin-bottom: 20px;
}

.profile-avatar {
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-accent) 100%);
  font-size: 32px;
  font-weight: 700;
}

.profile-basic h3 {
  margin: 0 0 6px;
  font-size: 18px;
  color: #1e293b;
}

.profile-form :deep(.el-input__wrapper) {
  border-radius: 10px;
}

/* ========== 响应式 ========== */
@media (max-width: 768px) {
  .sidebar {
    position: fixed;
    left: 0;
    top: 0;
    bottom: 0;
    z-index: 200;
    transform: translateX(0);
  }
  
  .sidebar-collapsed { transform: translateX(-100%); }
  
  .navbar { padding: 0 16px; }
  
  .user-info-text { display: none; }
  
  .content-wrapper { padding: 16px; }
  
  .footer-inner { 
    flex-direction: column; 
    gap: 4px;
  }
}
</style>
